import {
  AnalysisFeedbackPayload,
  ChangeReasonBanner,
  ConfidenceIndicator,
  CriterionDetailedQuestion,
  EvidenceCardConfig,
} from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";

import { QuestionForUI } from "../../data-model/cmmMedicalNeccessityQuestionnaire";

import type { QuestionPatch } from "./index";
import UserFeedBackOnAnswerChange from "./userFeedbackForAnswerChange";

export interface RadioQuestionProps {
  question: QuestionForUI;
  stepNumber: number;
  onChange: (patch: QuestionPatch) => void;
  disabled?: boolean;
  evidenceCards?: EvidenceCardConfig[];
  confidenceScore?: number;
  answerChangedReason?: string;
  shouldShowUserFeedBack?: boolean;
  onSubmit?: (
    payload: AnalysisFeedbackPayload & { questionName: string },
  ) => void;
  onCancel?: (payload: { questionName: string }) => void;
}

/**
 * Renders a `radio-subjective` question: a radio group plus an optional or
 * mandatory follow-up textarea driven by `shouldShowTextArea` /
 * `isTextAreaMandatory`. Free text travels alongside the selected choice via
 * the `free_text_answer` patch.
 */
export const RadioQuestion: React.FC<RadioQuestionProps> = ({
  question,
  stepNumber,
  onChange,
  disabled,
  evidenceCards,
  confidenceScore,
  answerChangedReason,
  shouldShowUserFeedBack = false,
  onSubmit,
  onCancel,
}) => {
  const handleSubmit = (payload: AnalysisFeedbackPayload) => {
    onSubmit?.({ ...payload, questionName: question.name });
  };
  const handleCancel = () => {
    onCancel?.({ questionName: question.name });
  };
  const radioValue = typeof question.answer === "string" ? question.answer : "";
  const shouldShowChangeReasonBanner =
    answerChangedReason && answerChangedReason.trim().length > 0;
  return (
    <CriterionDetailedQuestion
      id={question.name}
      stepNumber={stepNumber}
      title={question.question}
      radioName={question.name}
      radioOptions={question.options}
      radioValue={radioValue}
      radioDisabled={disabled}
      onRadioChange={(value) =>
        onChange({ name: question.name, answer: value })
      }
      textareaName={question.name}
      textareaValue={question.free_text_answer.answer}
      textareaDisabled={disabled}
      shouldHideTextBox={!question.shouldShowTextArea}
      isTextRequired={question.isTextAreaMandatory}
      onTextChange={(value) =>
        onChange({
          name: question.name,
          free_text_answer: {
            name: question.free_text_answer.name || radioValue,
            answer: value,
          },
        })
      }
      evidenceCards={evidenceCards}
      badge={
        <ConfidenceIndicator label="CONFIDENCE" value={confidenceScore ?? 0} />
      }
      headerSlot={
        shouldShowChangeReasonBanner ? (
          <ChangeReasonBanner
            label="ANSWER CHANGED REASON"
            reason={answerChangedReason}
            multiline={true}
          />
        ) : null
      }
      children={
        shouldShowUserFeedBack ? (
          <UserFeedBackOnAnswerChange
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        ) : null
      }
    />
  );
};

export default RadioQuestion;
